import React from "react";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import InputIcon from "@mui/icons-material/Input";
import { COLORS } from "./constants";
import { panelSx, sectionSubtleSx, sectionTitleSx } from "./styles";

// ─────────────────────────────────────────────────────────────────────────────
// SourcePanel
//
// RTMP ingest, SRT relay, browser encode and MP4 schedule are ALTERNATIVES —
// you use one at a time — but they used to sit in the left column as four
// equal stacked panels, all competing for attention and pushing everything
// else below the fold. This is the switcher that makes the relationship
// between them visible.
//
// IMPORTANT: the caller keeps all four children MOUNTED and hides the inactive
// ones with `display: none`, instead of rendering only the selected one.
// Unmounting would be destructive, not merely wasteful:
//   • BrowserEncodePanel owns a live getUserMedia MediaStream, a MediaRecorder
//     and the /browser-encode WebSocket. Unmounting it mid-broadcast tears all
//     three down — glancing at your RTMP credentials would end your stream.
//   • Mp4SchedulePanel holds file-input and selection state across a chunked
//     upload that can run for minutes.
// See SOURCES below for the ids; they are UI-only and never sent anywhere.
// ─────────────────────────────────────────────────────────────────────────────

export const SOURCES = [
  { id: "rtmp",    label: "RTMP",     hint: "Push from OBS, vMix or any RTMP encoder." },
  { id: "srt",     label: "SRT",      hint: "Lower latency than RTMP, encrypted camera-to-server." },
  { id: "browser", label: "Browser",  hint: "Go live straight from this tab — no encoder needed." },
  { id: "file",    label: "MP4 file", hint: "Schedule a recorded video to stream at a set time." }
];

// The card that holds everything about the input: the switcher, the preview
// and the selected source's settings (passed as children). These used to be
// three separate cards, with the switcher sitting between the picture and its
// own settings.
export function SourcePanel({ source, setSource, children }) {
  const active = SOURCES.find((s) => s.id === source) || SOURCES[0];

  return (
    <Box sx={{ ...panelSx, p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: { xs: "16px", sm: "20px" } }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}
        alignItems={{ md: "center" }} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}
          sx={{ display: { xs: "none", sm: "flex" } }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
            bgcolor: "rgba(43,189,175,0.12)", border: "1px solid rgba(43,189,175,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <InputIcon sx={{ color: COLORS.cyan, fontSize: 19 }} />
          </Box>
          <Box minWidth={0}>
            <Typography sx={sectionTitleSx}>Source</Typography>
            <Typography sx={sectionSubtleSx}>{active.hint}</Typography>
          </Box>
        </Stack>

        <ToggleButtonGroup
          exclusive
          value={source}
          onChange={(_e, next) => { if (next) setSource(next); }}
          aria-label="Stream source"
          sx={{
            bgcolor: COLORS.input,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "12px",
            p: 0.5,
            flexShrink: 0,
            alignSelf: { xs: "stretch", md: "center" },
            "& .MuiToggleButtonGroup-grouped": {
              flex: { xs: 1, md: "none" },
              border: "1px solid transparent !important",
              borderRadius: "9px !important",
              px: { xs: 1, md: 1.75 }, py: 0.75,
              minHeight: { xs: 40, md: 0 },
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: 14, md: 13.5 },
              whiteSpace: "nowrap",
              color: COLORS.muted,
              "&.Mui-selected": {
                bgcolor: COLORS.raised,
                color: COLORS.text,
                borderColor: `${COLORS.borderStrong} !important`,
                "&:hover": { bgcolor: COLORS.raised }
              },
              "&:hover": { bgcolor: "rgba(255,255,255,0.05)", color: COLORS.text }
            }
          }}
        >
          {SOURCES.map((s) => (
            <ToggleButton key={s.id} value={s.id} aria-label={s.label}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {children && <Box sx={{ mt: { xs: 1.5, sm: 3 } }}>{children}</Box>}
    </Box>
  );
}
